import React, { useEffect, useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useI18n } from '../i18n/I18nProvider';

const VocabularyActionDialog = ({ dialog, onClose }) => {
  const { t } = useI18n();
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setValue(dialog?.initialValue || '');
    setBusy(false);
    setError('');
  }, [dialog]);

  if (!dialog) return null;

  const needsText = dialog.kind !== 'confirm';
  const submit = async (event) => {
    event.preventDefault();
    const cleanValue = value.trim();
    if (needsText && !cleanValue) {
      setError(t('Please enter a name.'));
      return;
    }
    setBusy(true);
    setError('');
    try {
      await dialog.onConfirm(needsText ? cleanValue : undefined);
      onClose();
    } catch (submitError) {
      setError(submitError.message || t('This change could not be saved.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4">
      <form onSubmit={submit} className="w-full max-w-md overflow-hidden rounded-lg bg-white shadow-2xl" role="dialog" aria-modal="true" aria-labelledby="vocabulary-action-title">
        <header className="flex items-start justify-between border-b px-5 py-4">
          <div>
            <h2 id="vocabulary-action-title" className="text-lg font-bold text-gray-950">{t(dialog.title)}</h2>
            {dialog.description && <p className="mt-1 text-sm text-gray-600">{t(dialog.description)}</p>}
          </div>
          <button type="button" onClick={onClose} disabled={busy} className="rounded p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-900 disabled:opacity-50" title={t('Close')}>
            <XMarkIcon className="h-5 w-5" />
          </button>
        </header>

        <div className="px-5 py-5">
          {needsText && (
            <label className="block text-sm font-semibold text-gray-800">
              {t(dialog.inputLabel || 'Name')}
              <input
                autoFocus
                value={value}
                onChange={(event) => setValue(event.target.value)}
                className="mt-2 w-full rounded border border-gray-300 px-3 py-2.5 text-gray-950 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                maxLength={120}
              />
            </label>
          )}
          {error && <p role="alert" className="mt-3 border-l-4 border-red-500 bg-red-50 p-3 text-sm text-red-800">{error}</p>}
        </div>

        <footer className="flex justify-end gap-3 border-t bg-gray-50 px-5 py-4">
          <button type="button" onClick={onClose} disabled={busy} className="rounded border border-gray-300 bg-white px-4 py-2 font-semibold text-gray-700 hover:bg-gray-100 disabled:opacity-50">
            {t('Cancel')}
          </button>
          <button
            type="submit"
            disabled={busy || (needsText && !value.trim())}
            className={`rounded px-4 py-2 font-semibold text-white disabled:opacity-50 ${dialog.destructive ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'}`}
          >
            {busy ? t('Saving...') : t(dialog.confirmLabel || 'Save changes')}
          </button>
        </footer>
      </form>
    </div>
  );
};

export default VocabularyActionDialog;
